import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { availableLanguages } from '../../locales';
import useCooldown from '../vedic/shared/hooks/useCooldown';
import isHanumanDay from '../vedic/prashnavali/shared/utils/isHanumanDay';
import PathTracingGrid from '../vedic/prashnavali/shared/PathTracingGrid';
import PrashnavaliShareImagePreview from '../vedic/prashnavali/shared/PrashnavaliShareImagePreview';
import deriveHanumanReading from '../vedic/prashnavali/hanuman/utils/deriveHanumanReading';
import hanumanShareImageRenderer from '../vedic/prashnavali/hanuman/utils/hanumanShareImageRenderer';
import HanumanLanding from '../vedic/prashnavali/hanuman/HanumanLanding';
import HanumanResultCard from '../vedic/prashnavali/hanuman/HanumanResultCard';
import { HANUMAN_GRID_PALETTE, HANUMAN_COLORS } from '../vedic/prashnavali/hanuman/hanumanColors';
import hanumanData from '../vedic/prashnavali/hanuman/data/hanuman_prashnavali.json';
import useLocale from '../vedic/hooks/useLocale';

const COOLDOWN_STORAGE_KEY = 'prashnavali_hanuman_last_query';
const COOLDOWN_DURATION_MS = 60 * 60 * 1000;

const PageContainer = styled.main`
  min-height: 100vh;
  background: #FFFBF0;
  padding: 24px 20px 60px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}px) {
    padding: 16px 16px 40px;
  }
`;

const TopBar = styled.div`
  max-width: 720px;
  margin: 0 auto ${p => p.theme.spacing[4]}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${HANUMAN_COLORS.primaryDark};
  font-size: 14px;
  cursor: pointer;
`;

const LangToggle = styled.div`
  display: flex;
  gap: 4px;
`;

const LangBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid ${p => p.active ? HANUMAN_COLORS.primary : HANUMAN_COLORS.cellBorder};
  background: ${p => p.active ? HANUMAN_COLORS.primary : 'transparent'};
  color: ${p => p.active ? '#fff' : HANUMAN_COLORS.primaryDark};
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  font-family: 'Noto Sans Tamil', sans-serif;
`;

const GridSection = styled.section`
  max-width: 720px;
  margin: 0 auto;
  text-align: center;
`;

const GridHeader = styled.header`
  margin-bottom: ${p => p.theme.spacing[5]}px;
`;

const HeaderTitle = styled.h2`
  font-family: 'Noto Sans Tamil', 'Playfair Display', Georgia, serif;
  font-size: 20px;
  color: ${HANUMAN_COLORS.primaryDark};
  margin: 0 0 ${p => p.theme.spacing[2]}px;
`;

const HeaderPrompt = styled.p`
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  font-size: 14px;
  color: ${p => p.theme.colors.text.secondary};
  margin: 0;
`;

const HanumanPrashnavali = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('landing');
  const [lang, setLang] = useState('ta');
  const [selectedCellIndex, setSelectedCellIndex] = useState(null);
  const [sharePreviewOpen, setSharePreviewOpen] = useState(false);

  const t = useLocale(lang, 'hanumanPrashnavali');

  const cooldown = useCooldown({
    storageKey: COOLDOWN_STORAGE_KEY,
    durationMs: COOLDOWN_DURATION_MS,
  });

  const reading = useMemo(() => {
    if (selectedCellIndex === null) return null;
    return deriveHanumanReading(selectedCellIndex, hanumanData);
  }, [selectedCellIndex]);

  const handleBegin = useCallback(() => {
    if (cooldown.active) return;
    setView('grid');
  }, [cooldown.active]);

  const handlePathComplete = useCallback((cellIndex) => {
    setSelectedCellIndex(cellIndex);
    setView('result');
  }, []);

  const handleReturn = useCallback(() => {
    if (selectedCellIndex !== null) cooldown.startCooldown(selectedCellIndex);
    setSelectedCellIndex(null);
    setView('landing');
  }, [selectedCellIndex, cooldown]);

  const handleViewLastReading = useCallback(() => {
    if (cooldown.lastReadingCellIndex !== null) {
      setSelectedCellIndex(cooldown.lastReadingCellIndex);
      setView('result');
    }
  }, [cooldown.lastReadingCellIndex]);

  const gridCells = useMemo(() =>
    hanumanData.grid.map((primary, i) => ({
      primary,
      secondary: hanumanData.grid_ta[i],
    })), []);

  const pathForCell = useCallback((idx) => hanumanData.paths[idx], []);

  const renderImage = useCallback(
    () => hanumanShareImageRenderer(reading, lang, { isSpecialDay: isHanumanDay(new Date()) }),
    [reading, lang]
  );

  return (
    <PageContainer>
      <TopBar>
        <BackLink onClick={() => navigate('/vedic-tools')}>
          &larr; {lang === 'ta' ? 'கருவிகள்' : 'Tools'}
        </BackLink>
        <LangToggle>
          {availableLanguages.map(l => (
            <LangBtn key={l.code} active={lang === l.code} onClick={() => setLang(l.code)}>
              {l.label}
            </LangBtn>
          ))}
        </LangToggle>
      </TopBar>

      {view === 'landing' && (
        <HanumanLanding
          onBegin={handleBegin}
          onViewLastReading={handleViewLastReading}
          cooldown={cooldown}
          lang={lang}
        />
      )}

      {view === 'grid' && (
        <GridSection>
          <GridHeader>
            <HeaderTitle>{t.gridTitle || "Hanuman's Counsel"}</HeaderTitle>
            <HeaderPrompt>{t.gridPrompt || 'Hold your question in mind, and tap any cell'}</HeaderPrompt>
          </GridHeader>
          <PathTracingGrid
            size={7}
            cells={gridCells}
            palette={HANUMAN_GRID_PALETTE}
            pathForCell={pathForCell}
            onPathComplete={handlePathComplete}
            ariaLabel="Hanuman Prashnavali 7 by 7 grid"
          />
        </GridSection>
      )}

      {view === 'result' && reading && (
        <HanumanResultCard
          pathCells={reading.pathCells}
          collectedLetters={reading.collectedLetters}
          collectedLettersTa={reading.collectedLettersTa}
          verse={reading.verse}
          verse_ta={reading.verse_ta}
          chalisaLineNumber={reading.chalisaLineNumber}
          interpretation_ta={reading.interpretation_ta}
          interpretation_en={reading.interpretation_en}
          grid={hanumanData.grid}
          gridTa={hanumanData.grid_ta}
          onShare={() => setSharePreviewOpen(true)}
          onReturn={handleReturn}
          lang={lang}
        />
      )}

      {sharePreviewOpen && reading && (
        <PrashnavaliShareImagePreview
          open={sharePreviewOpen}
          onClose={() => setSharePreviewOpen(false)}
          renderImage={renderImage}
          shareTitle={t.shareTitle || "Hanuman's Counsel"}
          fileName="hanuman-prashnavali.png"
          lang={lang}
        />
      )}
    </PageContainer>
  );
};

export default HanumanPrashnavali;
